import { useEffect, useRef, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { showToast } from "@/lib/toast";
import {
  adSlotService,
  Banner,
  TextLink,
  BannerPosition,
  TextLinkPosition,
  BANNER_POSITION_LABEL,
  TEXTLINK_POSITION_LABEL,
} from "@/services/adSlotService";
import MultiSelect from "@/components/ui/multi-select";
import { imageService } from "@/services/imageService";
import { SITE_DOMAINS } from "@/constants/site-domains";

/** Cắt phần giờ để đưa vào input type="date"; backend nhận ISO nên gửi lại nguyên chuỗi. */
const toDateInput = (v?: string | null) => (v ? String(v).slice(0, 10) : "");

/** Banner/textlink chỉ hiện khi đang bật VÀ nằm trong khoảng hiệu lực. */
function runtimeState(item: { isActive: boolean; startAt?: string | null; endAt?: string | null }) {
  if (!item.isActive) return { label: "Tắt", variant: "secondary" as const };
  const now = Date.now();
  if (item.startAt && new Date(item.startAt).getTime() > now)
    return { label: "Chưa tới hạn", variant: "outline" as const };
  if (item.endAt && new Date(item.endAt).getTime() < now)
    return { label: "Hết hạn", variant: "destructive" as const };
  return { label: "Đang chạy", variant: "default" as const };
}

const emptyBanner: Partial<Banner> = {
  name: "",
  imageUrl: "",
  targetUrl: "",
  alt: "",
  position: BannerPosition.HEADER,
  order: 0,
  isActive: true,
  startAt: null,
  endAt: null,
  note: "",
  domains: ["phimmoihd.it.com"],
};

const emptyTextLink: Partial<TextLink> = {
  anchor: "",
  url: "",
  rel: "nofollow",
  target: "_blank",
  position: TextLinkPosition.FOOTER,
  order: 0,
  isActive: true,
  startAt: null,
  endAt: null,
  note: "",
  domains: ["phimmoihd.it.com"],
};

export default function AdSlotPage() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [textLinks, setTextLinks] = useState<TextLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [siteTab, setSiteTab] = useState<string>(SITE_DOMAINS[0].value);

  const [bannerForm, setBannerForm] = useState<Partial<Banner> | null>(null);
  const [linkForm, setLinkForm] = useState<Partial<TextLink> | null>(null);

  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Upload file rồi đổ URL trả về vào ô imageUrl. Backend chỉ nhận
   * jpeg/png/webp/gif và tối đa 10MB — chặn sớm ở đây để báo lỗi rõ ràng
   * thay vì để multer trả về lỗi khó hiểu.
   */
  const uploadBannerImage = async (file: File) => {
    const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!ALLOWED.includes(file.type)) {
      showToast.error("Chỉ nhận JPG, PNG, WEBP hoặc GIF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast.error(`Ảnh ${(file.size / 1024 / 1024).toFixed(1)}MB — vượt giới hạn 10MB`);
      return;
    }
    setUploadingImage(true);
    try {
      const res = await imageService.upload(file, {
        alt: file.name.replace(/\.[^/.]+$/, ""),
        title: file.name.replace(/\.[^/.]+$/, ""),
      });
      // Cùng kiểu lồng .data như fetchAll ở trên
      const data =
        (res as any)?.data?.data?.data ||
        (res as any)?.data?.data ||
        (res as any)?.data;
      const url = data?.url;
      if (!url) throw new Error("Response không có URL ảnh");
      setBannerForm((prev) => ({ ...prev, imageUrl: url }));
      showToast.success("Đã tải ảnh lên");
    } catch (e: any) {
      showToast.error(e?.message || "Tải ảnh lên thất bại");
    } finally {
      setUploadingImage(false);
      // Cho phép chọn lại đúng file vừa rồi (onChange không bắn nếu value không đổi)
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [b, t]: any[] = await Promise.all([
        adSlotService.getBanners(),
        adSlotService.getTextLinks(),
      ]);
      // 3 lớp .data lồng nhau: apiClient bọc response fetch, TransformInterceptor
      // của Nest bọc {statusCode,data}, rồi AdSlotService tự bọc thêm {status,data}
      // quanh mảng thật. Giữ luôn 2 mức nông hơn làm fallback phòng response đổi dạng.
      setBanners(b?.data?.data?.data ?? b?.data?.data ?? b?.data ?? []);
      setTextLinks(t?.data?.data?.data ?? t?.data?.data ?? t?.data ?? []);
    } catch (e: any) {
      showToast.error(e?.message || "Tải danh sách thất bại");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAll();
  }, []);

  const saveBanner = async () => {
    if (!bannerForm?.name || !bannerForm?.imageUrl || !bannerForm?.targetUrl) {
      showToast.error("Cần điền tên, ảnh và đường dẫn đích");
      return;
    }
    try {
      if (bannerForm._id) await adSlotService.updateBanner(bannerForm._id, bannerForm as any);
      else await adSlotService.createBanner(bannerForm as any);
      showToast.success("Đã lưu banner");
      setBannerForm(null);
      void fetchAll();
    } catch (e: any) {
      showToast.error(e?.message || "Lưu thất bại");
    }
  };

  const saveLink = async () => {
    if (!linkForm?.anchor || !linkForm?.url) {
      showToast.error("Cần điền chữ neo và đường dẫn");
      return;
    }
    try {
      if (linkForm._id) await adSlotService.updateTextLink(linkForm._id, linkForm as any);
      else await adSlotService.createTextLink(linkForm as any);
      showToast.success("Đã lưu textlink");
      setLinkForm(null);
      void fetchAll();
    } catch (e: any) {
      showToast.error(e?.message || "Lưu thất bại");
    }
  };

  const del = async (kind: "banner" | "link", id: string) => {
    if (!window.confirm("Xoá mục này?")) return;
    try {
      if (kind === "banner") await adSlotService.removeBanner(id);
      else await adSlotService.removeTextLink(id);
      showToast.success("Đã xoá");
      void fetchAll();
    } catch (e: any) {
      showToast.error(e?.message || "Xoá thất bại");
    }
  };

  const toggle = async (kind: "banner" | "link", item: any) => {
    try {
      const patch = { isActive: !item.isActive };
      if (kind === "banner") await adSlotService.updateBanner(item._id, patch);
      else await adSlotService.updateTextLink(item._id, patch);
      void fetchAll();
    } catch (e: any) {
      showToast.error(e?.message || "Không đổi được trạng thái");
    }
  };

  const bannersForSite = banners.filter((b) => b.domains?.includes(siteTab));
  const textLinksForSite = textLinks.filter((t) => t.domains?.includes(siteTab));

  return (
    <DashboardLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Chỗ quảng cáo</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Tab ngoài theo TỪNG SITE — banner/textlink của site nào chỉ hiện khi
              đang đứng ở tab site đó, tránh nhầm khách đặt site A ra site B. */}
          <Tabs value={siteTab} onValueChange={setSiteTab}>
            <TabsList>
              {SITE_DOMAINS.map((d) => (
                <TabsTrigger key={d.value} value={d.value}>{d.label}</TabsTrigger>
              ))}
            </TabsList>

            {SITE_DOMAINS.map((d) => (
              <TabsContent key={d.value} value={d.value}>
                <Tabs defaultValue="banners">
                  <TabsList>
                    <TabsTrigger value="banners">Banner ({bannersForSite.length})</TabsTrigger>
                    <TabsTrigger value="textlinks">Textlink ({textLinksForSite.length})</TabsTrigger>
                  </TabsList>

                  <TabsContent value="banners" className="space-y-3">
                    <Button onClick={() => setBannerForm({ ...emptyBanner, domains: [siteTab] })}>Thêm banner</Button>
                    {loading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
                    <div className="space-y-2">
                      {bannersForSite.map((b) => {
                        const st = runtimeState(b);
                        return (
                          <div key={b._id} className="flex items-center gap-3 rounded border p-3">
                            <img src={b.imageUrl} alt={b.alt || b.name} className="h-12 w-24 object-cover" />
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">{b.name}</div>
                              <div className="truncate text-xs text-muted-foreground">{b.targetUrl}</div>
                            </div>
                            <Badge variant="outline">{BANNER_POSITION_LABEL[b.position]}</Badge>
                            {b.domains?.length > 1 && (
                              <div className="flex flex-wrap gap-1" title="Cũng hiện trên site khác">
                                {b.domains.filter((x) => x !== siteTab).map((x) => (
                                  <Badge key={x} variant="secondary" className="text-xs">+ {x}</Badge>
                                ))}
                              </div>
                            )}
                            <Badge variant={st.variant}>{st.label}</Badge>
                            <Switch checked={b.isActive} onCheckedChange={() => toggle("banner", b)} />
                            <Button variant="outline" size="sm" onClick={() => setBannerForm({ ...b })}>Sửa</Button>
                            <Button variant="destructive" size="sm" onClick={() => del("banner", b._id)}>Xoá</Button>
                          </div>
                        );
                      })}
                      {!loading && !bannersForSite.length && (
                        <p className="text-sm text-muted-foreground">Site này chưa có banner nào.</p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="textlinks" className="space-y-3">
                    <Button onClick={() => setLinkForm({ ...emptyTextLink, domains: [siteTab] })}>Thêm textlink</Button>
                    <div className="space-y-2">
                      {textLinksForSite.map((t) => {
                        const st = runtimeState(t);
                        return (
                          <div key={t._id} className="flex items-center gap-3 rounded border p-3">
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">{t.anchor}</div>
                              <div className="truncate text-xs text-muted-foreground">{t.url}</div>
                            </div>
                            <Badge variant={t.rel?.includes("nofollow") ? "outline" : "destructive"}>
                              {t.rel || "—"}
                            </Badge>
                            <Badge variant="outline">{TEXTLINK_POSITION_LABEL[t.position]}</Badge>
                            {t.domains?.length > 1 && (
                              <div className="flex flex-wrap gap-1" title="Cũng hiện trên site khác">
                                {t.domains.filter((x) => x !== siteTab).map((x) => (
                                  <Badge key={x} variant="secondary" className="text-xs">+ {x}</Badge>
                                ))}
                              </div>
                            )}
                            <Badge variant={st.variant}>{st.label}</Badge>
                            <Switch checked={t.isActive} onCheckedChange={() => toggle("link", t)} />
                            <Button variant="outline" size="sm" onClick={() => setLinkForm({ ...t })}>Sửa</Button>
                            <Button variant="destructive" size="sm" onClick={() => del("link", t._id)}>Xoá</Button>
                          </div>
                        );
                      })}
                      {!loading && !textLinksForSite.length && (
                        <p className="text-sm text-muted-foreground">Site này chưa có textlink nào.</p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={!!bannerForm} onOpenChange={(o) => !o && setBannerForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{bannerForm?._id ? "Sửa banner" : "Thêm banner"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div><Label>Tên nội bộ</Label><Input value={bannerForm?.name || ""} onChange={(e) => setBannerForm({ ...bannerForm, name: e.target.value })} /></div>
            <div>
              <Label>Ảnh</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Dán URL, hoặc bấm Tải ảnh lên"
                  value={bannerForm?.imageUrl || ""}
                  onChange={(e) => setBannerForm({ ...bannerForm, imageUrl: e.target.value })}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploadingImage}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingImage ? "Đang tải..." : "Tải ảnh lên"}
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void uploadBannerImage(f);
                }}
              />
              {bannerForm?.imageUrl ? (
                <img
                  src={bannerForm.imageUrl}
                  alt="Xem trước"
                  className="mt-2 max-h-24 rounded border object-contain"
                />
              ) : null}
            </div>
            <div><Label>Đường dẫn đích</Label><Input value={bannerForm?.targetUrl || ""} onChange={(e) => setBannerForm({ ...bannerForm, targetUrl: e.target.value })} /></div>
            <div><Label>Vị trí</Label>
              <select className="w-full rounded border bg-background p-2" value={bannerForm?.position}
                onChange={(e) => setBannerForm({ ...bannerForm, position: e.target.value as BannerPosition })}>
                {Object.values(BannerPosition).map((p) => <option key={p} value={p}>{BANNER_POSITION_LABEL[p]}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bắt đầu</Label><Input type="date" value={toDateInput(bannerForm?.startAt)} onChange={(e) => setBannerForm({ ...bannerForm, startAt: e.target.value || null })} /></div>
              <div><Label>Kết thúc</Label><Input type="date" value={toDateInput(bannerForm?.endAt)} onChange={(e) => setBannerForm({ ...bannerForm, endAt: e.target.value || null })} /></div>
            </div>
            <div><Label>Thứ tự</Label><Input type="number" value={bannerForm?.order ?? 0} onChange={(e) => setBannerForm({ ...bannerForm, order: Number(e.target.value) })} /></div>
            <div>
              <Label>Hiện trên site</Label>
              <MultiSelect
                options={SITE_DOMAINS}
                placeholder="Chọn site hiển thị"
                value={bannerForm?.domains || []}
                onChange={(values) => setBannerForm({ ...bannerForm, domains: values })}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Khách mua chỗ trên site nào thì chọn đúng site đó — bỏ trống là banner không hiện ở đâu cả.
              </p>
            </div>
            <div><Label>Ghi chú nội bộ (khách, giá, kỳ hạn — không trả ra API)</Label><Input value={bannerForm?.note || ""} onChange={(e) => setBannerForm({ ...bannerForm, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBannerForm(null)}>Huỷ</Button>
            <Button onClick={saveBanner}>Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkForm} onOpenChange={(o) => !o && setLinkForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{linkForm?._id ? "Sửa textlink" : "Thêm textlink"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div><Label>Chữ neo</Label><Input value={linkForm?.anchor || ""} onChange={(e) => setLinkForm({ ...linkForm, anchor: e.target.value })} /></div>
            <div><Label>Đường dẫn</Label><Input value={linkForm?.url || ""} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} /></div>
            <div>
              <Label>Thuộc tính rel</Label>
              <Input value={linkForm?.rel || ""} onChange={(e) => setLinkForm({ ...linkForm, rel: e.target.value })} />
              <p className="mt-1 text-xs text-muted-foreground">
                Để <code>nofollow</code>. Link trả phí mà dofollow vi phạm nguyên tắc chống spam,
                và án phạt rơi vào chính site này chứ không phải site được trỏ tới.
              </p>
            </div>
            <div><Label>Vị trí</Label>
              <select className="w-full rounded border bg-background p-2" value={linkForm?.position}
                onChange={(e) => setLinkForm({ ...linkForm, position: e.target.value as TextLinkPosition })}>
                {Object.values(TextLinkPosition).map((p) => <option key={p} value={p}>{TEXTLINK_POSITION_LABEL[p]}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bắt đầu</Label><Input type="date" value={toDateInput(linkForm?.startAt)} onChange={(e) => setLinkForm({ ...linkForm, startAt: e.target.value || null })} /></div>
              <div><Label>Kết thúc</Label><Input type="date" value={toDateInput(linkForm?.endAt)} onChange={(e) => setLinkForm({ ...linkForm, endAt: e.target.value || null })} /></div>
            </div>
            <div><Label>Thứ tự</Label><Input type="number" value={linkForm?.order ?? 0} onChange={(e) => setLinkForm({ ...linkForm, order: Number(e.target.value) })} /></div>
            <div>
              <Label>Hiện trên site</Label>
              <MultiSelect
                options={SITE_DOMAINS}
                placeholder="Chọn site hiển thị"
                value={linkForm?.domains || []}
                onChange={(values) => setLinkForm({ ...linkForm, domains: values })}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Khách guestpost mua chỗ trên site nào thì chọn đúng site đó — bỏ trống là textlink không hiện ở đâu cả.
              </p>
            </div>
            <div><Label>Ghi chú nội bộ</Label><Input value={linkForm?.note || ""} onChange={(e) => setLinkForm({ ...linkForm, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkForm(null)}>Huỷ</Button>
            <Button onClick={saveLink}>Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
